import { Link } from "react-router-dom";
import {
  Phone,
  Users,
  ShieldCheck,
  Handshake,
  MapPin,
  Award,
  Hammer,
  Clock,
  ArrowRight,
} from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import SectionHeading from "@/components/SectionHeading";
import CheckList from "@/components/CheckList";
import CtaBand from "@/components/CtaBand";
import TrustBar from "@/components/TrustBar";
import { business, images } from "@/data/site";

const schema = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  name: "About Future Roofing and Repair",
  about: {
    "@type": "RoofingContractor",
    name: business.name,
    telephone: "+1-215-806-5038",
    email: business.email,
    founder: business.owner,
    areaServed: "Bucks County, Pennsylvania",
  },
};

const values = [
  {
    icon: Handshake,
    title: "Honest & Straightforward",
    body: "No pressure, no gimmicks. We give you clear answers, honest assessments, and fair pricing so you can make the right call for your home or business.",
  },
  {
    icon: Hammer,
    title: "Quality Workmanship",
    body: "13 years of hands-on experience on every job — from small repairs to complete roofing projects — done right the first time with quality materials.",
  },
  {
    icon: Users,
    title: "Family-Owned & Local",
    body: "We live and work in the same community we serve. When you call, you work directly with our family-run team, not a faceless call center.",
  },
  {
    icon: ShieldCheck,
    title: "Licensed & Insured",
    body: "We're licensed, insured, and GAF certified — for your protection and peace of mind.",
  },
];

const stats = [
  { value: "13", label: "Years of Experience" },
  { value: "Free", label: "Estimates Available" },
  { value: "GAF", label: "Certified Contractor" },
  { value: "100%", label: "Family-Owned & Local" },
];

const whatWeDo = [
  "Residential roofing",
  "Commercial roofing",
  "Roof repairs",
  "Flat, metal & shingle roofing",
  "Gutters, siding, fascia & soffit",
  "Insurance claim assistance",
];

export default function About() {
  return (
    <>
      <Seo
        title="About Future Roofing and Repair | Family-Owned Roofer in Bucks County, PA"
        description="Future Roofing and Repair is a family-owned, locally operated roofing company in Bristol, PA, led by Kyle McClinton with 13 years of roofing experience serving Bucks County."
        keywords="About Future Roofing and Repair, Family-owned roofing company, Kyle McClinton roofer, Roofer Bristol PA, Local roofing company Bucks County"
        schema={schema}
      />

      <PageHero
        kicker="About Us"
        title="Family-Owned. Locally Rooted."
        subtitle="Future Roofing and Repair is a family-owned roofing company built on honest work, quality craftsmanship, and 13 years of hands-on experience serving Bucks County."
        image={images.about}
      />

      <TrustBar />

      {/* Story */}
      <section className="bg-cream" data-testid="about-story">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <img
              src={images.about}
              alt="Roofer from Future Roofing and Repair on a rooftop in Bucks County"
              className="w-full h-[440px] object-cover border border-charcoal/20 shadow-md"
            />
            <div className="absolute -bottom-5 -right-3 sm:right-6 bg-rust text-cream border border-charcoal/20 shadow-md px-5 py-3 text-center">
              <span className="block font-heading text-4xl leading-none">13</span>
              <span className="font-accent uppercase text-xs tracking-widest">Years Experience</span>
            </div>
          </div>
          <div>
            <SectionHeading kicker="Our Story" title="Roofing Done the Right Way" />
            <div className="space-y-4 text-charcoal/80 leading-relaxed">
              <p>
                Future Roofing and Repair is a family-owned, locally operated roofing company based in
                Bristol, Pennsylvania, proudly serving Bucks County and the surrounding areas. We started
                this company with a simple belief: homeowners and businesses deserve roofing they can
                trust, from people who treat them like neighbors — because they are.
              </p>
              <p>
                Owner <strong className="text-charcoal">{business.owner}</strong> brings 13 years of
                hands-on roofing experience to every project, from quick leak repairs
                to complete residential and commercial roofing. That experience means fewer
                surprises, smarter solutions, and work that holds up over time.
              </p>
              <p>
                As a family-owned business, our name is on every roof we touch. We show up when we say we
                will, communicate clearly, clean up after the job, and stand behind our workmanship. No
                pressure, no overpromising — just dependable roofing from a team that cares.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 mt-7">
              <a
                href={business.phoneHref}
                data-testid="about-call"
                className="inline-flex items-center justify-center gap-2 bg-rust text-cream border border-charcoal/20 shadow-md hover:-translate-y-0.5 transition-all font-heading text-xl uppercase px-6 py-3"
              >
                <Phone className="w-5 h-5" /> Call {business.phone}
              </a>
              <Link
                to="/contact"
                data-testid="about-estimate"
                className="inline-flex items-center justify-center gap-2 bg-navy text-cream border border-charcoal/20 shadow-md hover:-translate-y-0.5 transition-all font-heading text-xl uppercase px-6 py-3"
              >
                Request an Estimate <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-charcoal relative grain border-y border-charcoal/15" data-testid="about-stats">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((s) => (
            <div key={s.label} className="text-center">
              <p className="font-heading text-5xl sm:text-6xl text-gold leading-none">{s.value}</p>
              <p className="font-accent uppercase text-xs tracking-widest text-cream/80 mt-2">
                {s.label}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Values */}
      <section className="bg-cream-200 border-b border-charcoal/15" data-testid="about-values">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <SectionHeading
            kicker="Why Homeowners Choose Us"
            title="What We Stand For"
            center
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {values.map((v) => (
              <div
                key={v.title}
                className="bg-cream border border-charcoal/20 shadow-md p-6 flex flex-col"
              >
                <span className="flex items-center justify-center w-12 h-12 bg-charcoal text-gold border border-charcoal/20 mb-4">
                  <v.icon className="w-6 h-6" />
                </span>
                <h3 className="font-heading text-2xl uppercase text-charcoal leading-none">{v.title}</h3>
                <p className="mt-2 text-sm text-charcoal/75 leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What we do + service area */}
      <section className="bg-cream" data-testid="about-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <SectionHeading kicker="What We Do" title="A Full-Service Roofing Company" />
            <p className="text-charcoal/80 leading-relaxed mb-6">
              From a single shingle to a full commercial roof system, we handle it all for homes and
              businesses across the region:
            </p>
            <CheckList items={whatWeDo} columns={2} />
          </div>
          <div className="bg-charcoal text-cream border border-charcoal/20 shadow-md p-8">
            <MapPin className="w-10 h-10 text-gold mb-4" />
            <h3 className="font-heading text-3xl uppercase">Proudly Serving Bucks County</h3>
            <p className="mt-4 text-cream/85 leading-relaxed">
              Based in Bristol, PA, Future Roofing and Repair serves {business.serviceArea}. Being local
              means faster response times, familiarity with our area's weather and building styles, and a
              team that's invested in the community we call home.
            </p>
            <ul className="mt-5 space-y-2 text-cream/85">
              <li className="flex items-center gap-2">
                <Award className="w-5 h-5 text-gold" /> GAF Certified Contractor
              </li>
              <li className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-gold" /> {business.hours}
              </li>
              <li className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-gold" /> {business.address.full}
              </li>
            </ul>
            <a
              href={business.phoneHref}
              data-testid="about-area-call"
              className="mt-6 inline-flex items-center gap-2 bg-rust text-cream border border-cream/40 shadow-md hover:-translate-y-0.5 transition-all font-heading text-xl uppercase px-6 py-3"
            >
              <Phone className="w-5 h-5" /> Talk to Our Team
            </a>
          </div>
        </div>
      </section>

      <CtaBand
        variant="default"
        kicker="Let's Work Together"
        title="Experience the Difference"
        text="Reliable, family-owned roofing backed by 13 years of experience. Call us or request a free estimate today."
      />
    </>
  );
}
